"use client";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Navbar,
  NavbarBrand,
  NavbarCollapse,
  NavbarToggle,
  NavbarLink,
  Sidebar,
} from "flowbite-react";
import Image from "next/image";
import { useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";

const Navigation = () => {
  const pathname = usePathname();
  const router = useRouter();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  let timeoutId: NodeJS.Timeout;

  const handleMouseEnter = (menu: string) => {
    clearTimeout(timeoutId);
    setOpenDropdown(menu);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setOpenDropdown(null);
    }, 300);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setSearchQuery("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim() !== "") {
      router.push(`/search?query=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
    }
  };

  return (
    <>
      <Navbar
        fluid
        rounded
        className="relative border-b bg-white font-sans dark:bg-gray-900"
      >
        <NavbarBrand href="/">
          <Image
            src="/logo-v2.jpeg"
            alt="Logo"
            width={48}
            height={48}
            className="h-12 w-auto"
          />
        </NavbarBrand>

        <NavbarCollapse className="hidden md:flex">
          <NavbarLink href="/" className={getActiveClass(pathname, "/")}>
            Accueil
          </NavbarLink>
          <NavbarLink
            href="/category/a-la-une"
            className={getActiveClass(pathname, "/a-la-une")}
          >
            À la Une
          </NavbarLink>

          {/* Décryptage */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("decryptage")}
            onMouseLeave={handleMouseLeave}
          >
            <NavbarLink href="#" className="cursor-pointer">
              Décryptage
            </NavbarLink>
            {openDropdown === "decryptage" && (
              <SubMenu
                onMouseEnter={() => handleMouseEnter("decryptage")}
                onMouseLeave={handleMouseLeave}
              >
                <SubMenuItem
                  href="/category/economie"
                  label="Économie"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/politique"
                  label="Politique"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/culture"
                  label="Culture"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/sport"
                  label="Sport"
                  pathname={pathname}
                />
              </SubMenu>
            )}
          </div>

          {/* Analyse */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("analyse")}
            onMouseLeave={handleMouseLeave}
          >
            <NavbarLink href="#" className="cursor-pointer">
              Analyse
            </NavbarLink>
            {openDropdown === "analyse" && (
              <SubMenu
                onMouseEnter={() => handleMouseEnter("analyse")}
                onMouseLeave={handleMouseLeave}
              >
                <SubMenuItem
                  href="/category/essais"
                  label="Essais"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/entretien"
                  label="Entretiens"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/opinion"
                  label="Opinions"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/nouvelle"
                  label="Nouvelle"
                  pathname={pathname}
                />
              </SubMenu>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter("video")}
            onMouseLeave={handleMouseLeave}
          >
            <NavbarLink href="#" className="cursor-pointer">
              Vidéos & Podcasts
            </NavbarLink>
            {openDropdown === "video" && (
              <SubMenu
                onMouseEnter={() => handleMouseEnter("video")}
                onMouseLeave={handleMouseLeave}
              >
                <SubMenuItem
                  href="/category/cafe-citoyen"
                  label="Café citoyen"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/angle-mort"
                  label="Angle mort"
                  pathname={pathname}
                />
                <SubMenuItem
                  href="/category/resume-articles"
                  label="Résumé Articles"
                  pathname={pathname}
                />
              </SubMenu>
            )}
          </div>

          {/* <NavbarLink
            href="/category/videos-podcasts"
            className={getActiveClass(pathname, "/videos-podcasts")}
          >
            Vidéos & Podcasts
          </NavbarLink> */}

          <NavbarLink
            href="/category/afrique"
            className={getActiveClass(pathname, "/afrique")}
          >
            Afrique
          </NavbarLink>
          <NavbarLink
            href="/category/international"
            className={getActiveClass(pathname, "/international")}
          >
            International
          </NavbarLink>
          <NavbarLink
            href="/about"
            className={getActiveClass(pathname, "/about")}
          >
            À propos
          </NavbarLink>
        </NavbarCollapse>

        {/* Right Side */}
        <div className="flex items-center space-x-4 md:order-2">
          {/* Search */}
          {!isSearchOpen && (
            <button
              onClick={toggleSearch}
              className="text-gray-700 hover:text-blue-500 dark:text-white"
              aria-label="Ouvrir la recherche"
            >
              <FaSearch size={20} />
            </button>
          )}

          {isSearchOpen && (
            <form onSubmit={handleSubmit} className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 rounded-md bg-gray-100 px-4 py-2 text-gray-900 transition-all focus:outline-none focus:ring focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                autoFocus
                onKeyDown={(e) => e.key === "Escape" && toggleSearch()}
              />
              <button
                type="button"
                onClick={toggleSearch}
                className="absolute right-2 top-2 text-gray-500 hover:text-red-500"
              >
                <FaTimes size={16} />
              </button>
            </form>
          )}

          {/* Mobile toggle button */}
          <button
            onClick={() => setShowSidebar(true)}
            className="block text-gray-700 dark:text-white md:hidden"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </Navbar>

      {/* 📱 Sidebar shown only on mobile */}
      {showSidebar && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setShowSidebar(false)}
          ></div>

          {/* Sidebar */}
          <div className="relative z-50 h-full w-64 translate-x-0 transform bg-white transition-transform duration-300 ease-in-out dark:bg-gray-800">
            <Sidebar aria-label="Mobile navigation" className="h-full">
              <div className="flex justify-end p-4">
                <button
                  onClick={() => setShowSidebar(false)}
                  className="text-gray-500 hover:text-red-500"
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <Sidebar.Items>
                <Sidebar.ItemGroup>
                  <Sidebar.Item href="/">Accueil</Sidebar.Item>
                  <Sidebar.Item href="/category/a-la-une">
                    À la Une
                  </Sidebar.Item>

                  <details>
                    <summary className="cursor-pointer px-3 py-2">
                      Décryptage
                    </summary>
                    <div className="ml-4">
                      <Sidebar.Item href="/category/economie">
                        Économie
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/politique">
                        Politique
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/culture">
                        Culture
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/sport">Sport</Sidebar.Item>
                    </div>
                  </details>

                  <details>
                    <summary className="cursor-pointer px-3 py-2">
                      Analyse
                    </summary>
                    <div className="ml-4">
                      <Sidebar.Item href="/category/essais">
                        Essais
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/entretien">
                        Entretiens
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/opinion">
                        Opinions
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/nouvelle">
                        Nouvelle
                      </Sidebar.Item>
                    </div>
                  </details>

                  <details>
                    <summary className="cursor-pointer px-3 py-2">
                      Vidéos & Podcasts
                    </summary>
                    <div className="ml-4">
                      <Sidebar.Item href="/category/cafe-citoyen">
                        Café citoyen
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/angle-mort">
                        Angle mort
                      </Sidebar.Item>
                      <Sidebar.Item href="/category/resume-article">
                        Résumé Articles
                      </Sidebar.Item>
                    </div>
                  </details>

                  {/* <Sidebar.Item href="/category/videos-podcasts">
                    Vidéos & Podcasts
                  </Sidebar.Item> */}
                  <Sidebar.Item href="/category/afrique">Afrique</Sidebar.Item>
                  <Sidebar.Item href="/category/international">
                    International
                  </Sidebar.Item>
                  <Sidebar.Item href="/about">À propos</Sidebar.Item>
                </Sidebar.ItemGroup>
              </Sidebar.Items>
            </Sidebar>
          </div>
        </div>
      )}
    </>
  );
};

const getActiveClass = (pathname: string, href: string) =>
  `text-gray-900 dark:text-white font-medium ${
    pathname === href ? "text-blue-600 dark:text-blue-400" : ""
  }`;

const SubMenu = ({
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  children: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => (
  <div
    className="absolute left-0 z-50 mt-2 w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    <ul className="py-2">{children}</ul>
  </div>
);

const SubMenuItem = ({
  href,
  label,
  pathname,
}: {
  href: string;
  label: string;
  pathname: string;
}) => (
  <li>
    <Link
      href={href}
      className={`block px-4 py-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700 ${
        pathname === href ? "text-blue-600 dark:text-blue-400" : ""
      }`}
    >
      {label}
    </Link>
  </li>
);

export default Navigation;
